import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useTelegramWebApp } from "../../hooks/useTelegramWebApp.js";
import {
  NetworkError,
  UploadMediaError,
  uploadMedia,
  type MediaKind,
} from "../../lib/api-client.js";
import { reportClientError } from "../../lib/client-error.js";
import { logger } from "../../lib/logger.js";
import {
  fileToDataUrl,
  generatePhotoThumbnail,
} from "../../lib/media/thumbnail.js";
import { useMediaStore } from "../../stores/media-store.js";
import { showToast } from "../../stores/toast-store.js";

const ACCEPT: Record<MediaKind, string> = {
  photo: "image/jpeg,image/png,image/webp,image/gif",
  video: "video/mp4,video/quicktime,video/webm",
  audio: "audio/mpeg,audio/mp4,audio/aac,audio/x-m4a",
};

const MAX_BYTES: Record<MediaKind, number> = {
  photo: 10 * 1024 * 1024,
  video: 50 * 1024 * 1024,
  audio: 50 * 1024 * 1024,
};

function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    // Native file pickers fire `change` on selection and nothing on cancel.
    // Listen for `cancel` (Chrome 113+) and fall back to a focus heuristic.
    let resolved = false;
    const finish = (file: File | null) => {
      if (resolved) return;
      resolved = true;
      resolve(file);
    };
    input.addEventListener("change", () => {
      finish(input.files?.[0] ?? null);
    });
    input.addEventListener("cancel", () => {
      finish(null);
    });
    // If neither event fires (older Safari), cleanup after a focus regain.
    const onFocus = () => {
      window.removeEventListener("focus", onFocus);
      // Wait a tick for the change event to win.
      window.setTimeout(() => {
        finish(input.files?.[0] ?? null);
      }, 300);
    };
    window.addEventListener("focus", onFocus);
    input.click();
  });
}

function uploadErrorToToastKey(err: unknown): string {
  if (err instanceof UploadMediaError) {
    if (err.code === "bot_chat_unavailable") {
      return "media.errors.bot_chat_unavailable";
    }
    if (err.code === "file_too_large") {
      return "media.errors.too_large";
    }
    if (err.code === "invalid_mime") {
      return "media.errors.invalid_mime";
    }
    if (err.code === "empty_file") {
      return "media.errors.empty_file";
    }
    return "media.errors.upload_failed";
  }
  if (err instanceof NetworkError) {
    return "share.errors.network";
  }
  return "media.errors.upload_failed";
}

/**
 * Returns a callback that opens the OS file picker for the given media
 * kind, kicks off the upload, and updates `useMediaStore` through its
 * status transitions. Surfaces user-facing errors via the toast store.
 */
export function useMediaPicker() {
  const wa = useTelegramWebApp();
  const { t } = useTranslation();
  const setUploading = useMediaStore((s) => s.setUploading);
  const setReady = useMediaStore((s) => s.setReady);
  const setError = useMediaStore((s) => s.setError);
  const setRetryAttempt = useMediaStore((s) => s.setRetryAttempt);
  const clear = useMediaStore((s) => s.clear);

  return useCallback(
    async (kind: MediaKind) => {
      const file = await pickFile(ACCEPT[kind]);
      if (!file) return;

      if (file.size === 0) {
        showToast(t("media.errors.empty_file"), "error");
        return;
      }
      if (file.size > MAX_BYTES[kind]) {
        showToast(
          t("media.errors.too_large", {
            limit_mb: Math.round(MAX_BYTES[kind] / 1024 / 1024),
          }),
          "error",
        );
        return;
      }

      const initData = wa?.initData;
      if (!initData) {
        showToast(t("share.errors.auth"), "error");
        return;
      }

      const id = globalThis.crypto.randomUUID();
      const blobUrl = URL.createObjectURL(file);
      // Generate two photo previews up-front (in parallel):
      //  - thumbDataUrl: small data: URL used by the slot AND persisted
      //    with the draft so the preview survives reopen.
      //  - originalDataUrl: full-quality data: URL kept in memory only,
      //    used by the Telegram preview mosaic so the user sees their
      //    actual photo. data: works on Android Telegram where blob:
      //    in <img> is unreliable.
      // Failures fall back gracefully — the upload itself doesn't depend
      // on either preview generating.
      const [thumbResult, originalResult] =
        kind === "photo"
          ? await Promise.all([
              generatePhotoThumbnail(file),
              fileToDataUrl(file),
            ])
          : [
              { url: null, trace: ["non_photo"] },
              { url: null, trace: ["non_photo"] },
            ];
      const thumbDataUrl = thumbResult.url;
      const originalDataUrl = originalResult.url;

      // Beacon a snapshot of what got generated so Android-only
      // rendering bugs can be diagnosed from K8s logs without a
      // remote-debug session. Truncates URLs so the payload fits in
      // the 8KB stack cap.
      reportClientError({
        source: "manual",
        message: `media_picker_setup kind=${kind} size=${String(file.size)} thumb=${String(thumbDataUrl !== null)} orig=${String(originalDataUrl !== null)}`,
        stack: JSON.stringify({
          tag: "media_picker_setup",
          kind,
          name: file.name.slice(0, 100),
          mime: file.type || "(empty)",
          size: file.size,
          blob_url_prefix: blobUrl.slice(0, 60),
          has_thumb: thumbDataUrl !== null,
          thumb_len: thumbDataUrl?.length ?? 0,
          thumb_prefix: thumbDataUrl?.slice(0, 50) ?? null,
          thumb_trace: thumbResult.trace,
          has_original: originalDataUrl !== null,
          original_len: originalDataUrl?.length ?? 0,
          original_prefix: originalDataUrl?.slice(0, 50) ?? null,
          original_trace: originalResult.trace,
          create_image_bitmap: typeof createImageBitmap === "function",
          file_array_buffer:
            typeof (file as File & { arrayBuffer?: unknown }).arrayBuffer ===
            "function",
          ua: navigator.userAgent.slice(0, 200),
        }),
      });

      setUploading({
        id,
        kind,
        blobUrl,
        name: file.name,
        mime: file.type || "application/octet-stream",
        size: file.size,
        ...(thumbDataUrl ? { thumbDataUrl } : {}),
        ...(originalDataUrl ? { originalDataUrl } : {}),
      });

      try {
        const res = await uploadMedia({
          file,
          kind,
          initData,
          onRetry: (attempt, err) => {
            // `attempt` is the (1-based) attempt number that just
            // failed — the next attempt is `attempt + 1`. Surface that
            // in the slot so the user sees we're still trying.
            setRetryAttempt(id, attempt + 1);
            logger.warn("media upload retry", { attempt, error: err });
          },
        });
        // Server thumb wins on Android (where local generation fails);
        // ignored if the picker already produced a local one. Setter
        // does the prefer-local merge.
        setReady(id, res.file_id, res.thumb_dataurl);
      } catch (err) {
        // Surface the bot's request_id so a user-reported failure can
        // be matched against the K8s log line in upload-media.ts.
        const requestId =
          err instanceof UploadMediaError
            ? (err.extra.request_id as string | undefined)
            : undefined;
        logger.error("media upload failed", {
          err,
          request_id: requestId,
          status:
            err instanceof UploadMediaError ? err.status : undefined,
        });
        const key = uploadErrorToToastKey(err);
        const code =
          err instanceof UploadMediaError ? err.code : "upload_failed";
        setError(id, code);
        showToast(t(key), "error");
        // For a fully failed upload, drop the slot so the user can retry
        // without first manually clearing it.
        clear();
        wa.HapticFeedback.notificationOccurred("error");
      }
    },
    [wa, t, setUploading, setReady, setError, clear],
  );
}
